import React, { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, Polygon, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { PublicAmenitiesPayload } from '../../../services/superPickerPublicApi';

/**
 * Public-side ProjectMap. Mirrors `super-picker/components/map/ProjectMap.tsx`
 * but consumes the masked `PublicAmenitiesPayload` instead of the agent
 * `AmenitiesResponse`. Logic and visual styling (custom markers, radius
 * circle, project building icon) are intentionally identical.
 */

export interface SelectedAmenityTypes {
  mrtLrt: boolean;
  schools: boolean;
  preschools: boolean;
  supermarkets: boolean;
}

export type ProjectDetailsTab = 'details' | 'amenities' | 'files';

interface ProjectMapProps {
  latitude: number;
  longitude: number;
  tab: ProjectDetailsTab;
  amenitiesData: PublicAmenitiesPayload | null;
  /** Radius in meters. */
  radius: number;
  selectedAmenityTypes: SelectedAmenityTypes;
}

/** GeoJSON [lng, lat] ring -> Leaflet [lat, lng] positions. */
const toLatLngs = (ring: Array<[number, number]>): [number, number][] =>
  ring.map(([lng, lat]) => [lat, lng] as [number, number]);

const ProjectMap: React.FC<ProjectMapProps> = ({
  latitude,
  longitude,
  tab,
  amenitiesData,
  radius,
  selectedAmenityTypes,
}) => {
  // The site outline and the area the backend actually filtered against. Both
  // are absent when OneMap has no land lot for the pin, in which case the map
  // falls back to the old circle drawn round the point.
  const lotRings = amenitiesData?.project?.boundary ?? null;
  const searchAreaRing = amenitiesData?.project?.searchArea ?? null;

  const mapRef = useRef<L.Map | null>(null);

  useEffect(() => {
    if (!mapRef.current) return;
    if (tab === 'amenities') {
      // Fit to the real search area when we have it — it is off-centre from the
      // pin, so a circle's bounds would crop one side.
      if (searchAreaRing && searchAreaRing.length > 2) {
        mapRef.current.fitBounds(L.latLngBounds(toLatLngs(searchAreaRing)), { padding: [20, 20] });
        return;
      }
      const center = L.latLng(latitude, longitude);
      const circleBounds = center.toBounds(radius * 2);
      mapRef.current.fitBounds(circleBounds, { padding: [20, 20] });
    } else {
      mapRef.current.setView([latitude, longitude], 15);
    }
  }, [latitude, longitude, tab, radius, searchAreaRing]);

  const MapController: React.FC = () => {
    const map = useMap();
    useEffect(() => {
      mapRef.current = map;
    }, [map]);
    return null;
  };

  const createIcon = (color: string) =>
    L.divIcon({
      className: 'custom-marker',
      html: `<div style="background-color: ${color}; width: 20px; height: 20px; border-radius: 50%; border: 2px solid #fff; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
      iconSize: [20, 20],
      iconAnchor: [10, 10],
    });

  const projectIcon = L.divIcon({
    className: 'custom-marker',
    html: `<div style="background-color: #1976d2; width: 32px; height: 32px; border-radius: 4px; border: 2px solid #fff; box-shadow: 0 2px 4px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center;">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="#fff" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"/>
      </svg>
    </div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });

  return (
    <MapContainer
      center={[latitude, longitude]}
      zoom={15}
      // No hard `minHeight` here — the wrapping container in
      // `ProjectInfoBanner` already provides a sensible floor on `md+`
      // (and a fixed height on `xs`). Letting the map fill 100% means
      // it scales to the bottom of the Details / Amenities content.
      style={{ height: '100%', width: '100%' }}
    >
      <MapController />
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />

      {/* Project marker — always shown */}
      <Marker position={[latitude, longitude]} icon={projectIcon}>
        <Popup>
          <strong>Project Location</strong>
        </Popup>
      </Marker>

      {/* Search area — the site grown outwards by the radius, which is what the
          amenity list was filtered against. Falls back to a circle round the pin
          when OneMap has no land lot for this project. */}
      {tab === 'amenities' && (
        searchAreaRing && searchAreaRing.length > 2 ? (
          <Polygon
            positions={toLatLngs(searchAreaRing)}
            pathOptions={{ color: '#1976d2', fillColor: '#1976d2', fillOpacity: 0.1, weight: 2 }}
          />
        ) : (
          <Circle
            center={[latitude, longitude]}
            radius={radius}
            pathOptions={{ color: '#1976d2', fillColor: '#1976d2', fillOpacity: 0.1, weight: 2 }}
          />
        )
      )}

      {/* Site outline — the land lot the distances are measured from */}
      {tab === 'amenities' && lotRings?.map((ring, i) => (
        <Polygon
          key={`lot-${i}`}
          positions={toLatLngs(ring)}
          pathOptions={{ color: '#ed6c02', fillColor: '#ed6c02', fillOpacity: 0.35, weight: 2 }}
        />
      ))}

      {/* Amenity markers */}
      {tab === 'amenities' && amenitiesData && (
        <>
          {selectedAmenityTypes.mrtLrt &&
            amenitiesData.amenities.mrtLrt.map((station) => (
              <Marker
                key={`mrt-${station.id}`}
                position={[station.latitude, station.longitude]}
                icon={createIcon('#d32f2f')}
              >
                <Popup>
                  <strong>{station.name}</strong>
                  <br />
                  <span>{Math.round(station.distance)}m away</span>
                </Popup>
              </Marker>
            ))}
          {selectedAmenityTypes.schools &&
            amenitiesData.amenities.schools.map((school) => (
              <Marker
                key={`school-${school.id}`}
                position={[school.latitude, school.longitude]}
                icon={createIcon('#388e3c')}
              >
                <Popup>
                  <strong>{school.name}</strong>
                  <br />
                  {school.type && <span>{school.type}</span>}
                  <br />
                  <span>{Math.round(school.distance)}m away</span>
                </Popup>
              </Marker>
            ))}
          {selectedAmenityTypes.preschools &&
            amenitiesData.amenities.preschools.map((preschool) => (
              <Marker
                key={`preschool-${preschool.id}`}
                position={[preschool.latitude, preschool.longitude]}
                icon={createIcon('#7b1fa2')}
              >
                <Popup>
                  <strong>{preschool.name}</strong>
                  <br />
                  {preschool.type && <span>{preschool.type}</span>}
                  {preschool.address && (
                    <>
                      <br />
                      <span>{preschool.address}</span>
                    </>
                  )}
                  <br />
                  <span>{Math.round(preschool.distance)}m away</span>
                </Popup>
              </Marker>
            ))}
          {selectedAmenityTypes.supermarkets &&
            amenitiesData.amenities.supermarkets.map((mart) => (
              <Marker
                key={`supermarket-${mart.id}`}
                position={[mart.latitude, mart.longitude]}
                icon={createIcon('#f57c00')}
              >
                <Popup>
                  <strong>{mart.name}</strong>
                  {mart.address && (
                    <>
                      <br />
                      <span>{mart.address}</span>
                    </>
                  )}
                  <br />
                  <span>{Math.round(mart.distance)}m away</span>
                </Popup>
              </Marker>
            ))}
        </>
      )}
    </MapContainer>
  );
};

export default ProjectMap;
